import type { CalendarUpsertSchema } from '@repo/schemas/meeting/calendarUpsert';
import type { Dispatch, SetStateAction } from 'react';

import { usePostMeeting } from '@repo/api/meeting/usePostMeeting';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { LoaderCircleIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';

import { MeetingFormContent } from '@/components/features/calendar/form/meeting-form-content/MeetingFormContent';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

interface CreateMeetingProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  defaultValues?: Partial<CalendarUpsertSchema>;
}

export default function CreateMeeting(createMeetingProps: CreateMeetingProps) {
  const { open, onOpenChange, defaultValues } = createMeetingProps;
  const navigate = useNavigate();
  const location = useLocation();

  const { mutateAsync: createMeeting, isPending } = usePostMeeting();

  const clearLocationState = () => {
    navigate({ pathname: location.pathname, search: location.search }, { replace: true });
  };

  const handleClose = () => {
    clearLocationState();
    onOpenChange(false);
  };

  const handleSubmit = async (data: CalendarUpsertSchema) => {
    const { users, groups, location, ...meeting } = data;
    const userArr = users.map((user) => user.id);
    const groupArr = groups.map((group) => group.id);
    const locationId = location?.id ?? null;

    await createMeeting({
      ...meeting,
      userArr,
      groupArr,
      locationId,
    })
      .then(() => {
        handleClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error as Error);
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'max-h-200 sm:h-[90dvh] sm:min-h-96 sm:max-w-120'} onCloseAutoFocus={handleClose} onEscapeKeyDown={handleClose} isClosable={false}>
        {isPending && (
          <div className={'bg-background/75 absolute inset-0 z-60 m-auto flex flex-wrap items-center justify-center gap-3 backdrop-blur-md'} role="status" aria-live="polite">
            <LoaderCircleIcon className={'animate-spin'} aria-hidden={'true'} />
            <h5 className={'font-medium'}>Trwa zapisywanie zmian...</h5>
          </div>
        )}
        <MeetingFormContent callback={handleSubmit} open={open} onOpenChange={onOpenChange} defaultValues={defaultValues} />
      </DialogContent>
    </Dialog>
  );
}
